import React, { JSX } from "react";
import { useTranslation } from "react-i18next";
import { LinearProgress } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFile } from "@fortawesome/free-regular-svg-icons";

export interface UploadPreviewState {
  file: File;
  // Object URL for browser-renderable images (image/*); null for pdf/ai/eps,
  // which cannot be painted by an <img> and get a file card instead.
  previewUrl: string | null;
}

// Decides whether a file can be shown through URL.createObjectURL in an <img>.
// The check is on the MIME type the browser reports, NOT the file extension:
// a WebP saved with a .png name (Intersport 7/9) reports image/webp and still
// renders; a pdf/ai/eps never does, whatever its name says.
export function canPreviewAsImage(file: Pick<File, "type">): boolean {
  return /^image\/(png|jpe?g|gif|webp|bmp|svg\+xml)$/i.test(file.type ?? "");
}

function formatSize(bytes: number): string {
  if (!(bytes > 0)) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Flag REACT_APP_LOGO_EDITOR_OPTIMISTIC_PREVIEW: what the standalone editor
 * shows while the two upload calls (create + upload) run. The user's own
 * file is painted at once in the same canvas box the editor uses, with a
 * thin indeterminate progress line under the header - no full-screen
 * spinner. Purely presentational: the parent owns the object URL lifetime.
 */
export function UploadPreview({
  file,
  previewUrl,
}: UploadPreviewState): JSX.Element {
  const { t } = useTranslation();
  const size = formatSize(file.size);
  return (
    <div className={"h-full flex flex-col"} data-testid="upload-preview">
      <div className={"bg-gray-100 space-y-2 py-2 px-3 rounded-tr-md"}>
        <div className={"flex h-9 items-center space-x-3"}>
          <h2 className={"flex-1 font-bold text-gray-600"}>
            {t("logoEditorComponents.uploadLogoHeader")}
          </h2>
          <span className={"text-xs text-gray-600 truncate max-w-[50%]"}>
            {t("logoEditorComponents.uploadingFile", { fileName: file.name })}
          </span>
        </div>
        <LinearProgress
          aria-label={t("logoEditorComponents.uploadingFile", {
            fileName: file.name,
          })}
          sx={{ height: 3, borderRadius: 2 }}
        />
      </div>
      <div className={"flex-1 flex justify-center items-center relative p-6"}>
        {previewUrl ? (
          <img
            src={previewUrl}
            alt={file.name}
            className={
              "min-h-[325px] min-w-[450px] max-h-[650px] max-w-[900px] object-contain bg-white rounded-bl opacity-90"
            }
            onContextMenu={(e) => e.preventDefault()}
          />
        ) : (
          <div
            className={
              "flex flex-col items-center justify-center gap-2 rounded border border-dashed border-gray-300 bg-white min-h-[325px] min-w-[450px] p-6 text-gray-600"
            }
            title={file.name}
          >
            <FontAwesomeIcon icon={faFile} size={"3x"} className={"opacity-50"} />
            <span className={"text-sm font-medium truncate max-w-[400px]"}>
              {file.name}
            </span>
            {size && <span className={"text-xs text-gray-500"}>{size}</span>}
            <span className={"text-xs italic text-gray-500"}>
              {t("logoEditorComponents.previewNotAvailableForFile")}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
